/* ============================================================================
   Yoga Nidra — SHARED CONTENT
   Read by the tutorial slide (11_yogaNidra.tsx), its portrait text layout
   (YogaNidraMobile.tsx) and the after-login practice panel
   (11_yogaNidra_body.tsx). Edit copy ONLY here — English and German side by
   side; the German object must have the same shape. Body-part names of the
   scan live in yogaNidraScan.ts.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";
import { NIDRA_SCAN_SUMMARY } from "./yogaNidraScan";

export const accentNidra = "#c090e0";

export interface Seg { t: string; b?: boolean; em?: boolean; hl?: boolean; }

const en = {
  meta: {
    breadcrumbPre: "Conscious Sleep",
    breadcrumbAccent: "Body Scan · Feet to Crown",
    headerTitlePre: "Yoga ",
    headerTitleAccent: "Nidra",
    sanskrit: "योग निद्रा",
    titlePre: "Yoga Nidra ",
    titleAccentWord: "(Conscious Sleep)",
    titlePost: "",
    techniqueLabel: "Technique",
    thresholdLabel: "The Threshold",
    guideBreadcrumb: "Guided Practice",
    guideSubtitle: "Body scan · feet to crown · stay aware",
  },
  /** Header of the after-login practice panel (11_yogaNidra_body.tsx). */
  body: {
    breadcrumbPre: "Guided Practice",
    breadcrumbAccent: "Conscious sleep · the final threshold",
  },
  technique: [
    { n: "01", title: "Lie Down & Relax", desc: "In Shavasana — close your eyes, take slow deep breaths. Let every muscle soften and the body completely release.", sub: null },
    { n: "02", title: "Set an Intention (Sankalpa)", desc: "Silently repeat: 'I will recognise my dreams and remain aware while dreaming.'", sub: null },
    { n: "03", title: "Body Awareness", desc: "Systematically scan from feet to crown — inhale to focus, exhale to release. Repeat continuously until sleep arrives.", sub: NIDRA_SCAN_SUMMARY.en },
    { n: "04", title: "Breath Awareness", desc: "Observe the natural rhythm of breath without controlling it. Let it become slow and effortless.", sub: null },
    { n: "05", title: "Enter the Dream State", desc: "Visualise a dream scene or let spontaneous images arise. Stay aware but relaxed — a silent witness.", sub: null },
    { n: "06", title: "Transition Consciously", desc: "Let the body drift into sleep while keeping the mind alert. Step into the dream with full awareness.", sub: null },
  ] as { n: string; title: string; desc: string; sub: string | null }[],
  intro: [
    { t: "A powerful relaxation technique that induces deep rest while maintaining awareness — supporting " },
    { t: "stress relief", hl: true },
    { t: ", " },
    { t: "emotional wellness", hl: true },
    { t: ", and deep rejuvenation." },
  ] as Seg[],
  threshold: [
    { t: "When awareness reaches the crown and the body dissolves into stillness — that edge between waking and sleep " },
    { t: "is where Dreamfulness begins.", b: true },
  ] as Seg[],
  /** Text drawn on the body-scan canvas. */
  canvas: {
    inhale: "INHALE",
    exhale: "EXHALE",
    feet: "FEET",
    crown: "CROWN",
    point: (n: number, of: number) => `${n} · of ${of}`,
  },
};

export type NidraContent = typeof en;

const de: NidraContent = {
  meta: {
    breadcrumbPre: "Bewusster Schlaf",
    breadcrumbAccent: "Body-Scan · Füße bis Scheitel",
    headerTitlePre: "Yoga ",
    headerTitleAccent: "Nidra",
    sanskrit: "योग निद्रा",
    titlePre: "Yoga Nidra ",
    titleAccentWord: "(Bewusster Schlaf)",
    titlePost: "",
    techniqueLabel: "Technik",
    thresholdLabel: "Die Schwelle",
    guideBreadcrumb: "Geführte Übung",
    guideSubtitle: "Body-Scan · Füße bis Scheitel · bleib bewusst",
  },
  body: {
    breadcrumbPre: "Geführte Übung",
    breadcrumbAccent: "Bewusster Schlaf · die letzte Schwelle",
  },
  technique: [
    { n: "01", title: "Hinlegen & entspannen", desc: "In Shavasana – schließe die Augen und atme langsam und tief. Lass jeden Muskel weich werden und den Körper ganz loslassen.", sub: null },
    { n: "02", title: "Eine Absicht setzen (Sankalpa)", desc: "Wiederhole innerlich: „Ich erkenne meine Träume und bleibe im Traum bewusst.“", sub: null },
    { n: "03", title: "Körperwahrnehmung", desc: "Wandere systematisch von den Füßen bis zum Scheitel – einatmen und wahrnehmen, ausatmen und loslassen. Wiederhole es, bis der Schlaf kommt.", sub: NIDRA_SCAN_SUMMARY.de },
    { n: "04", title: "Atembewusstsein", desc: "Beobachte den natürlichen Rhythmus des Atems, ohne ihn zu steuern. Lass ihn langsam und mühelos werden.", sub: null },
    { n: "05", title: "In den Traumzustand gleiten", desc: "Stell dir eine Traumszene vor oder lass Bilder von selbst entstehen. Bleib wach und doch entspannt – ein stiller Zeuge.", sub: null },
    { n: "06", title: "Bewusst hinübergehen", desc: "Lass den Körper in den Schlaf sinken, während der Geist wach bleibt. Tritt mit vollem Bewusstsein in den Traum.", sub: null },
  ],
  intro: [
    { t: "Eine kraftvolle Entspannungstechnik, die tiefe Ruhe bei wachem Bewusstsein schenkt – sie unterstützt " },
    { t: "Stressabbau", hl: true },
    { t: ", " },
    { t: "emotionales Wohlbefinden", hl: true },
    { t: " und tiefe Regeneration." },
  ],
  threshold: [
    { t: "Wenn das Bewusstsein den Scheitel erreicht und der Körper sich in Stille auflöst – an dieser Grenze zwischen Wachen und Schlafen " },
    { t: "beginnt Dreamfulness.", b: true },
  ],
  canvas: {
    inhale: "EINATMEN",
    exhale: "AUSATMEN",
    feet: "FÜSSE",
    crown: "SCHEITEL",
    point: (n: number, of: number) => `${n} · von ${of}`,
  },
};

export const nidraContent: ByLang<NidraContent> = { en, de };
export const useNidra = () => useL(nidraContent);
